import DOMPurify from 'dompurify';
import { DateTime } from 'luxon';
import { marked } from 'marked';

import {
  MAI_CHAT_TOOL_FALLBACK_PREFIX,
  MAI_CHAT_TOOL_LABELS,
} from './maiChat.constants';
import {
  MaiCitation,
  MaiMessage,
  MaiMessageSegment,
  MaiToolCall,
  MaiToolCallStatus,
} from './maiChat.types';
import { ToolResultFrame, ToolResultSource } from './maiSocket.frames';

// GFM prose only — anything outside this list is stripped by DOMPurify.
const ALLOWED_TAGS = [
  'a',
  'blockquote',
  'br',
  'code',
  'del',
  'em',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'li',
  'ol',
  'p',
  'pre',
  'strong',
  'table',
  'tbody',
  'td',
  'th',
  'thead',
  'tr',
  'ul',
];

const ALLOWED_ATTR = ['href'];

// Own instance keeps the hook scoped to Mai rendering. New-tab links keep the
// chat session open.
const maiPurify = DOMPurify();
maiPurify.setConfig({ ALLOWED_ATTR, ALLOWED_TAGS });
maiPurify.addHook('afterSanitizeAttributes', (node) => {
  const href = node.getAttribute('href');
  if (node.tagName === 'A' && href && !/^(#|mailto:|tel:)/i.test(href)) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

// Sanitized GFM → HTML for the one dangerouslySetInnerHTML consumer.
export const renderMaiMarkdown = (text: string): string =>
  maiPurify.sanitize(marked.parse(text, { async: false, gfm: true }));

// Wire contract with the mai prompt (runtime_agent/prompts/system_prompt.md):
// `[[cite:type|date|source]]`. `]` is excluded so adjacent markers can't merge.
const CITATION_TOKEN = /\[\[cite:([^\]]*)\]\]/g;

export interface ParsedCitation {
  type: string;
  date: string;
  source: string;
}

export const parseCitationToken = (inner: string): ParsedCitation | null => {
  const parts = inner.split('|');
  if (parts.length !== 3) return null;
  const [type, date, source] = parts.map((p) => p.trim());
  if (!type && !source) return null;
  return { type, date, source };
};

export const citationPillLabel = ({
  type,
  date,
  source,
}: ParsedCitation): string => [type, date, source].filter(Boolean).join(' · ');

const chartCitationKey = ({ type, date, source }: ParsedCitation): string =>
  `${type}|${date}|${source}`;

export interface ExtractedCitations {
  text: string;
  citations: ParsedCitation[];
  // Per occurrence, not deduped: every unparseable marker stays visible in the
  // prose, so each one is its own defect.
  malformedCount: number;
}

export const extractCitations = (raw: string): ExtractedCitations => {
  const citations: ParsedCitation[] = [];
  const seen = new Set<string>();
  let malformedCount = 0;
  let stripped = false;
  const text = raw.replace(CITATION_TOKEN, (match, inner) => {
    const parsed = parseCitationToken(inner);
    if (!parsed) {
      malformedCount += 1;
      return match;
    }
    stripped = true;
    const key = chartCitationKey(parsed);
    if (!seen.has(key)) {
      seen.add(key);
      citations.push(parsed);
    }
    return '';
  });
  if (!stripped) return { text: raw, citations, malformedCount };
  const cleaned = text
    .replace(/ +([.,;:!?)])/g, '$1')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
  return { text: cleaned, citations, malformedCount };
};

// Per-segment, not joined: a marker split across segments must stay malformed.
export const collectChartCitations = (texts: string[]): ParsedCitation[] => {
  const citations: ParsedCitation[] = [];
  const seen = new Set<string>();
  texts.forEach((text) => {
    extractCitations(text).citations.forEach((citation) => {
      const key = chartCitationKey(citation);
      if (seen.has(key)) return;
      seen.add(key);
      citations.push(citation);
    });
  });
  return citations;
};

const isTextSegment = (
  segment: MaiMessageSegment,
): segment is Extract<MaiMessageSegment, { kind: 'text' }> =>
  segment.kind === 'text';

const isToolsSegment = (
  segment: MaiMessageSegment,
): segment is Extract<MaiMessageSegment, { kind: 'tools' }> =>
  segment.kind === 'tools';

export interface ResponseSummary {
  toolCount: number;
  toolNames: string[];
  kbCitationCount: number;
  chartCitationCount: number;
  malformedCitationCount: number;
  segmentCount: number;
}

// Analytics rollup of a finished turn: counts and tool names only — citation
// payloads carry dates and sources, so they stay out of the event. Malformed
// markers are counted as a prompt-adherence signal.
export const summarizeResponse = (message: MaiMessage): ResponseSummary => {
  const segments = message.segments ?? [];
  const textSegments = segments.filter(isTextSegment).map((s) => s.text);
  const toolNames = segments
    .filter(isToolsSegment)
    .flatMap((s) => s.toolCalls.map((call) => call.name));

  return {
    toolCount: toolNames.length,
    toolNames,
    kbCitationCount: message.citations?.length ?? 0,
    // Chart citations dedupe (one pill per chart item) while malformed markers
    // don't (each stays visible) — both mirror what the clinician sees.
    chartCitationCount: collectChartCitations(textSegments).length,
    malformedCitationCount: textSegments.reduce(
      (total, text) => total + extractCitations(text).malformedCount,
      0,
    ),
    segmentCount: segments.length,
  };
};

// "Just now" within a minute either way (a server clock a few seconds ahead
// still reads as just sent), "2:14 PM" otherwise today, then "May 5"; the year
// is added outside the current one. Empty for an unparseable timestamp.
export const formatMessageTimestamp = (createdAt: string): string => {
  const sentAt = DateTime.fromISO(createdAt);
  if (!sentAt.isValid) return '';
  const now = DateTime.now();
  if (Math.abs(now.diff(sentAt, 'minutes').minutes) < 1) return 'Just now';
  if (sentAt.hasSame(now, 'day')) return sentAt.toFormat('h:mm a');
  return sentAt.toFormat(sentAt.hasSame(now, 'year') ? 'MMM d' : 'MMM d, yyyy');
};

// Full date and time for the timestamp's hover title: "Oct 26, 2026, 4:12 PM".
export const formatMessageFullTimestamp = (createdAt: string): string =>
  DateTime.fromISO(createdAt).toFormat('MMM d, yyyy, h:mm a');

// Curated label for a known tool, else a humanized name so a new backend tool
// never surfaces a raw identifier (get_transcripts → "Getting transcripts").
export const toolCallLabel = (name: string): string => {
  const known = MAI_CHAT_TOOL_LABELS[name];
  if (known) return known;
  const words = name
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .trim()
    .toLowerCase()
    .split(' ')
    .filter((word) => word !== 'get');
  return `${MAI_CHAT_TOOL_FALLBACK_PREFIX}${words.join(' ')}`.trimEnd();
};

export const buildCitationLabel = (citation: MaiCitation): string =>
  citation.page === null
    ? citation.file
    : `${citation.file} · p.${citation.page}`;

// Appends to the trailing text run, or opens a new one after a tool group.
export const appendTextSegment = (
  segments: MaiMessageSegment[],
  token: string,
): MaiMessageSegment[] => {
  const last = segments[segments.length - 1];
  if (last?.kind === 'text') {
    return [
      ...segments.slice(0, -1),
      { kind: 'text', text: last.text + token },
    ];
  }
  return [...segments, { kind: 'text', text: token }];
};

// Appends to the trailing tool group (or opens one after a text run), deduped
// by toolUseId — Strands re-fires a tool_call as its input streams in.
export const appendToolCallSegment = (
  segments: MaiMessageSegment[],
  toolCall: MaiToolCall,
): MaiMessageSegment[] => {
  const last = segments[segments.length - 1];
  if (last?.kind === 'tools') {
    if (last.toolCalls.some((t) => t.toolUseId === toolCall.toolUseId)) {
      return segments;
    }
    return [
      ...segments.slice(0, -1),
      { kind: 'tools', toolCalls: [...last.toolCalls, toolCall] },
    ];
  }
  return [...segments, { kind: 'tools', toolCalls: [toolCall] }];
};

const mapToolCalls = (
  segments: MaiMessageSegment[],
  update: (toolCall: MaiToolCall) => MaiToolCall,
): MaiMessageSegment[] =>
  segments.map((segment) =>
    segment.kind === 'tools'
      ? { kind: 'tools', toolCalls: segment.toolCalls.map(update) }
      : segment,
  );

// Flips matching tool calls to their terminal result status; 'pending' results
// are ignored so the step keeps spinning.
export const applyToolResults = (
  segments: MaiMessageSegment[],
  results: ToolResultFrame['results'],
): MaiMessageSegment[] => {
  const statusByUseId = new Map<string, MaiToolCallStatus>();
  results.forEach((result) => {
    if (result.status !== 'pending') {
      statusByUseId.set(result.toolUseId, result.status);
    }
  });
  if (statusByUseId.size === 0) return segments;
  return mapToolCalls(segments, (toolCall) => {
    const status = statusByUseId.get(toolCall.toolUseId);
    return status ? { ...toolCall, status } : toolCall;
  });
};

// Fallback for turn end: settle any still-pending tool calls to success.
export const settlePendingToolCalls = (
  segments: MaiMessageSegment[],
): MaiMessageSegment[] =>
  mapToolCalls(segments, (toolCall) =>
    toolCall.status === 'pending'
      ? { ...toolCall, status: 'success' }
      : toolCall,
  );

const citationKey = (citation: MaiCitation): string =>
  `${citation.file}|${citation.page}|${citation.url}`;

// Merges sources into existing citations (WS isn't deduped server-side), keyed
// by file+page+url per the AC so a repeated doc url across pages keeps each page.
export const dedupeCitations = (
  sources: ToolResultSource[],
  existing: MaiCitation[] = [],
): MaiCitation[] => {
  const seen = new Set(existing.map(citationKey));
  const merged = [...existing];
  sources.forEach((source) => {
    const key = citationKey(source);
    if (seen.has(key)) return;
    seen.add(key);
    merged.push({ file: source.file, page: source.page, url: source.url });
  });
  return merged;
};
